import { getTotalTokens } from '@kyumo/common';

/**
 * 数値をカンマ区切りでフォーマット
 */
export function formatNumber(num: number): string {
  return num.toLocaleString('ja-JP');
}

/**
 * 金額をドル形式でフォーマット
 */
export function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/**
 * コストをフォーマット（小さい金額は4桁表示）
 */
export function formatCost(cost: number): string {
  if (cost < 0.01) {
    return `$${cost.toFixed(4)}`;
  }
  return `$${cost.toFixed(2)}`;
}

/**
 * 現在の月を YYYY-MM 形式で取得
 */
export function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * 月を加減算
 */
export function adjustMonth(month: string, delta: number): string {
  const parts = month.split('-').map(Number);
  const year = parts[0] ?? new Date().getFullYear();
  const m = parts[1] ?? new Date().getMonth() + 1;
  const date = new Date(year, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * 月を表示用にフォーマット
 */
export function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  return `${year}年${Number(m)}月`;
}

/**
 * Y軸の最大値をキリの良い数値に丸める
 */
export function niceMax(dataMax: number): number {
  if (dataMax === 0) return 10;
  const margin = dataMax * 1.1;
  const magnitude = 10 ** Math.floor(Math.log10(margin));
  const normalized = margin / magnitude;
  let nice: number;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

interface DailyDataItem {
  date: string;
  inputTokens?: number | undefined;
  cacheReadTokens?: number | undefined;
  cacheWriteTokens?: number | undefined;
  outputTokens?: number | undefined;
  requestCount: number;
  cost: number;
}

interface PeriodInfo {
  year?: number | undefined;
  month?: number | undefined;
}

/**
 * 日別データをグラフ用に整形（全日分を0埋め）
 */
export function formatDailyChartData(dailyData: DailyDataItem[], period?: PeriodInfo) {
  const dataMap = new Map<number, { tokens: number; requests: number; cost: number }>();
  for (const d of dailyData) {
    const day = Number(d.date.split('-')[2]);
    const tokens = getTotalTokens(d);
    dataMap.set(day, {
      tokens,
      requests: d.requestCount,
      cost: d.cost,
    });
  }
  const daysInMonth = new Date(
    period?.year || new Date().getFullYear(),
    period?.month || new Date().getMonth() + 1,
    0,
  ).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const data = dataMap.get(day);
    return {
      date: String(day),
      トークン数: data?.tokens || 0,
      リクエスト数: data?.requests || 0,
      コスト: data?.cost || 0,
    };
  });
}

interface MonthlyTrendItem {
  month?: string | undefined;
  inputTokens?: number | undefined;
  cacheReadTokens?: number | undefined;
  cacheWriteTokens?: number | undefined;
  outputTokens?: number | undefined;
  requestCount: number;
  cost: number;
}

/**
 * 月キー（YYYY-MM）をX軸ラベル用の「M月」に変換
 */
export function formatMonthTick(month: string): string {
  return `${Number(month.split('-')[1])}月`;
}

/**
 * 月別推移データをグラフ用に整形（過去13ヶ月分を0埋め）
 *
 * 13ヶ月分だと先頭と末尾が同じ「M月」になるため、month には一意な YYYY-MM を入れ、
 * 表示上のラベルは formatMonthTick / formatMonth で変換する。
 * （ラベルが重複すると Recharts のツールチップが先頭の同名月の値を表示してしまう）
 */
export function formatMonthlyTrendData(monthlyTrend: MonthlyTrendItem[], period?: PeriodInfo) {
  const dataMap = new Map<string, { tokens: number; requests: number; cost: number }>();
  for (const d of monthlyTrend) {
    if (d.month) {
      const tokens = getTotalTokens(d);
      dataMap.set(d.month, {
        tokens,
        requests: d.requestCount,
        cost: d.cost,
      });
    }
  }
  const result = [];
  const now = new Date();
  const baseYear = period?.year || now.getFullYear();
  const baseMonth = period?.month || now.getMonth() + 1;
  for (let i = 12; i >= 0; i--) {
    const date = new Date(baseYear, baseMonth - 1 - i, 1);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const data = dataMap.get(monthKey);
    result.push({
      month: monthKey,
      トークン数: data?.tokens || 0,
      リクエスト数: data?.requests || 0,
      コスト: data?.cost || 0,
    });
  }
  return result;
}

/**
 * X軸ラベルの間引き設定（幅に応じて自動で間引く）
 */
export const xAxisTickProps = {
  interval: 'preserveStartEnd' as const,
  minTickGap: 24,
};

/**
 * グラフのTooltipスタイル
 */
export const tooltipStyle = {
  contentStyle: {
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '8px',
  },
  labelStyle: { color: '#fff' },
  itemStyle: { color: '#fff' },
  cursor: { fill: 'rgba(255, 255, 255, 0.1)' },
};
